import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { WaitlistForm } from './WaitlistForm';
import { RequestAccessModal } from './RequestAccessModal';

export function CoursesPage() {
  const [waitlistModal, setWaitlistModal] = useState<{isOpen: boolean, courseType: 'advanced' | 'premium' | null}>({
    isOpen: false,
    courseType: null
  });
  const [showSignupModal, setShowSignupModal] = useState(false);

  const openWaitlist = (courseType: 'advanced' | 'premium') => {
    setWaitlistModal({ isOpen: true, courseType });
  };

  const closeWaitlist = () => {
    setWaitlistModal({ isOpen: false, courseType: null });
  };

  const handleWaitlistSuccess = () => {
    closeWaitlist();
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <nav className="landing-nav">
            <Link to="/" className="logo">
              <img 
                src="/logo.png" 
                alt="LeetBerry" 
                className="landing-logo-image"
              />
            </Link>
            <div className="nav-links">
              <Link to="/courses" className="nav-link active">Courses</Link>
              <Link to="/faq" className="nav-link">FAQ</Link>
              <Link to="/auth" className="nav-link">Login</Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Courses Section */}
      <div className="landing-courses" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
        <div className="landing-container">
          <div className="section-header">
            <div className="section-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              <span>Learning Paths</span>
            </div>
            <div className="section-header-content">
              <div className="section-header-left">
                <h1 className="section-title">
                  Choose Your Journey to Recruitment Mastery
                </h1>
              </div>
              <div className="section-header-right">
                <p className="section-description">
                  From beginner-friendly foundations to advanced strategic mastery, our tiered approach ensures your team gets exactly the training they need.
                </p>
              </div>
            </div>
          </div>
          
          <div className="course-cards">
            <div className="course-card course-card-free">
              <div className="course-badge">
                <span>FREE</span>
              </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </div>
              <h3 className="course-title">Introduction to Senior IT Hiring</h3>
              <p className="course-description">
                Master foundational strategies to source and hire principal IT talent, cutting time-to-hire to &lt;60 days and boosting candidate quality.
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>8 beginner-friendly modules</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>CZ/US market insights (63%/74% shortages)</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>20% response rate from basic outreach</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Saves €15K in agency fees or €150K in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
                <Link 
                  to="/free-course-signup"
                  className="btn btn-primary btn-full"
                >
                  Start Free Now
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>

            <div className="course-card course-card-advanced">
              <div className="course-badge">
                <span>COMING SOON</span>
              </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="course-title">Deep-Dive Strategies</h3>
              <p className="course-description">
                Elevate your team with in-depth tools for sourcing, screening, and hiring seniors. Achieve 35–40% more applications and 30% faster hires.
                <br /><br />
                <em>Expected launch: Q2/Q3 2026</em>
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Multi-page guides, tasks, and examples</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Advanced Boolean, A/B testing, and competency models</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>80% time savings with AI tools</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Saves €100K-€150K in fees or €2M+ in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
                <button 
                  onClick={() => openWaitlist('advanced')}
                  className="btn btn-secondary btn-full"
                >
                  Join Waitlist
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="course-card course-card-premium">
              <div className="course-badge">
                <span>NOT AVAILABLE YET</span>
              </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
              </div>
              <h3 className="course-title">Super-Powered Recruiter Masterclass</h3>
              <p className="course-description">
                Transform your team into strategic TA leaders with a premium intensive 1-month program. Master AI-driven sourcing, business development, and client acquisition.
                <br /><br />
                <em>Advanced graduates get discounts • Direct buyers get 1-year Advanced access</em>
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>500+ pages, lessons, and 1:1 feedback</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>50% faster hires, 40% higher retention</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>Build a scalable recruitment business (3x ROI)</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>Saves €400K+ in fees or €10M+ in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
                <button 
                  onClick={() => openWaitlist('premium')}
                  className="btn btn-premium btn-full"
                >
                  I am interested
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Modal */}
      {waitlistModal.isOpen && waitlistModal.courseType && (
        <WaitlistForm
          courseType={waitlistModal.courseType}
          onClose={closeWaitlist}
          onSuccess={handleWaitlistSuccess}
        />
      )}

      {/* Signup Modal */}
      <RequestAccessModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onSuccess={() => setShowSignupModal(false)}
      />
    </div>
  );
}

